import * as React from "react";
import { makeClient } from "../urql";
import {
  Status,
  DisconnectEventDetails,
} from "../other/subscriptionClientWithStatus";
import { Client } from "urql";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/browser";

export type status =
  | "CONNECTING"
  | "CONNECTED"
  | { type: "DISCONNECTED"; details?: DisconnectEventDetails };

const getCloseCodeDescription = (code?: number): string => {
  if (!code) return "Unknown";

  const closeCodeMap: Record<number, string> = {
    1000: "Normal Closure",
    1001: "Going Away (server shutdown or navigation)",
    1002: "Protocol Error",
    1003: "Unsupported Data",
    1005: "No Status Received",
    1006: "Abnormal Closure (connection lost)",
    1007: "Invalid Frame Payload Data",
    1008: "Policy Violation",
    1009: "Message Too Big",
    1010: "Mandatory Extension Missing",
    1011: "Internal Server Error",
    1012: "Service Restart",
    1013: "Try Again Later",
    1014: "Bad Gateway",
    1015: "TLS Handshake Failure",
  };

  return closeCodeMap[code] || `Custom Code ${code}`;
};

export const useUrqlClient = (): [status, Client] | null => {
  const { i18n } = useTranslation();
  const [status, setStatus] = React.useState<status>();
  const clientRef = React.useRef<ReturnType<typeof makeClient> | null>(null);
  const disconnectDetailsRef = React.useRef<DisconnectEventDetails>();

  React.useEffect(() => {
    clientRef.current = makeClient(i18n);
    const [subscriptionClient, , destroy] = clientRef.current;
    const getInitialStatus = (): status => {
      switch (subscriptionClient.connectionStatus) {
        case Status.Disconnected:
          return {
            type: "DISCONNECTED",
            details: disconnectDetailsRef.current,
          };
        case Status.Connecting:
        case Status.Reconnecting:
          return "CONNECTING";
        case Status.Connected:
        case Status.Reconnected:
          return "CONNECTED";
      }
    };
    setStatus(getInitialStatus());

    const listeners: Array<Function> = [];
    listeners.push(
      subscriptionClient.onConnecting(() => {
        setStatus("CONNECTING");
      })
    );
    listeners.push(
      subscriptionClient.onConnected(() => {
        setStatus("CONNECTED");
        // Clear disconnect details on reconnection
        disconnectDetailsRef.current = undefined;
      })
    );
    listeners.push(
      subscriptionClient.onError((error: Error) => {
        console.error("Error on subscription", error);

        // Log WebSocket errors to Sentry only in production
        if (process.env.NODE_ENV === "production") {
          Sentry.captureException(error, {
            tags: {
              component: "WebSocket",
              eventType: "subscription_error",
            },
            level: "error",
          });
        }
      })
    );
    listeners.push(
      subscriptionClient.onReconnecting(() => {
        setStatus("CONNECTING");
      })
    );
    listeners.push(
      subscriptionClient.onReconnected(() => {
        setStatus("CONNECTED");
        // Clear disconnect details on reconnection
        disconnectDetailsRef.current = undefined;
      })
    );

    // Register handler for detailed disconnect events
    listeners.push(
      subscriptionClient.onDisconnectEvent((details) => {
        const closeCodeDescription = getCloseCodeDescription(details.code);

        console.log("WebSocket disconnected:", {
          code: details.code,
          codeDescription: closeCodeDescription,
          reason: details.reason,
          wasClean: details.wasClean,
        });

        // Store disconnect details
        disconnectDetailsRef.current = details;

        // Send disconnect event to Sentry only in production
        if (process.env.NODE_ENV === "production") {
          Sentry.captureMessage("WebSocket Disconnected", {
            level: details.wasClean ? "info" : "warning",
            tags: {
              component: "WebSocket",
              eventType: "disconnect",
              closeCode: details.code?.toString() || "unknown",
              wasClean: details.wasClean ? "true" : "false",
            },
            contexts: {
              disconnection: {
                code: details.code,
                codeDescription: closeCodeDescription,
                reason: details.reason || "No reason provided",
                wasClean: details.wasClean,
              },
            },
          });
        }
      })
    );

    listeners.push(
      subscriptionClient.onDisconnected(() => {
        setStatus({
          type: "DISCONNECTED",
          details: disconnectDetailsRef.current,
        });
      })
    );

    return () => {
      clientRef.current = null;
      listeners.forEach((listener) => listener());
      destroy();
    };
  }, [i18n]);

  if (clientRef.current === null || status === undefined) return null;

  const [, client] = clientRef.current;
  return [status, client];
};
